import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { colors, spacing, fontStyles } from '../../../styles';
import { ToolRendererProps, StockData } from './types';
import { LoadingState } from './shared/LoadingState';
import { formatLargeNumber } from './shared/utils';

// Custom hook for safe router usage
const useSafeHistory = () => {
  try {
    return useHistory();
  } catch {
    return null;
  }
};

// Format change percentage with sign
const formatChange = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Get change color
const getChangeColor = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return colors.silver;
  if (value > 0) return colors.leaf;
  if (value < 0) return colors.berry;
  return colors.silver;
};

interface ExtendedStockData extends StockData {
  changeYtd?: number;
}

export const StockInfoRenderer: FC<ToolRendererProps> = ({ output, isLoading, toolName }) => {
  const history = useSafeHistory();

  // Memoize expensive array operations for performance - must be before early returns
  const validSymbols = useMemo(() => {
    try {
      const symbolsArray = output.symbols_data || output.symbols;
      if (!symbolsArray || !Array.isArray(symbolsArray) || symbolsArray.length === 0) {
        return [];
      }

      return symbolsArray
        .map((symbolData): ExtendedStockData | null => {
          try {
            const stockData = (symbolData as any).basic_metadata || symbolData;
            if (!stockData || typeof stockData !== 'object') return null;

            const symbol = (stockData as any).symbol || (stockData as any).ticker || 'N/A';
            const name = (stockData as any).name || (stockData as any).company_name || '';
            const price = (stockData as any).latest_price || (stockData as any).price || (stockData as any).last_price;
            const changeYtd = (stockData as any).change_percent_ytd;
            const marketCap = (stockData as any).market_cap;

            return { symbol, name, price, changeYtd, marketCap };
          } catch (error) {
            console.warn('Failed to parse symbol data:', error);
            return null;
          }
        })
        .filter((stock): stock is ExtendedStockData => stock !== null);
    } catch (error) {
      console.error('Failed to parse symbols array:', error);
      return [];
    }
  }, [output.symbols_data, output.symbols]);

  if (isLoading) {
    const symbol = toolName?.includes('metadata') ? 'stock' : 'symbol';
    return <LoadingState message={`Getting ${symbol} info...`} />;
  }

  const handleSymbolClick = (symbol: string) => {
    // Validate symbol format (alphanumeric + common separators, reasonable length)
    const isValidSymbol = /^[A-Z0-9\-\.]{1,10}$/i.test(symbol);
    if (symbol && symbol !== 'N/A' && isValidSymbol && history) {
      history.push(`/symbol/${encodeURIComponent(symbol)}`);
    }
  };

  // Handle new format where data is in symbols/symbols_data array with basic_metadata
  const symbolsArray = output.symbols_data || output.symbols;
  if (symbolsArray && Array.isArray(symbolsArray) && symbolsArray.length > 0) {
    if (validSymbols.length === 0) {
      return <LoadingState message="Stock info retrieved" />;
    }

    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.liquorice};

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {validSymbols.map((stock: ExtendedStockData, index: number) => (
          <div
            key={stock.symbol}
            css={css`
              padding: ${spacing.centi} ${spacing.deci};
              ${index < validSymbols.length - 1 ? `border-bottom: 1px solid ${colors.cloud};` : ''}

              html[data-theme='dark'] & {
                border-color: ${colors.tungsten};
              }
            `}
          >
            {/* Row 1: Symbol and YTD Change */}
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: ${spacing.milli};
              `}
            >
              <button
                type="button"
                css={css`
                  background: none;
                  border: none;
                  padding: 0;
                  font-weight: 600;
                  color: ${colors.petrolBlue};
                  cursor: pointer;
                  text-decoration: none;
                  text-align: left;
                  font-size: inherit;
                  font-family: inherit;

                  &:hover,
                  &:focus {
                    text-decoration: underline;
                  }

                  &:focus {
                    outline: 2px solid ${colors.petrolBlue};
                    outline-offset: 2px;
                  }

                  html[data-theme='dark'] & {
                    color: ${colors.skyBlue};

                    &:focus {
                      outline-color: ${colors.skyBlue};
                    }
                  }
                `}
                onClick={() => handleSymbolClick(stock.symbol)}
                aria-label={`View details for ${stock.symbol} - ${stock.name}`}
              >
                {stock.symbol}
              </button>
              <span
                css={css`
                  font-weight: 500;
                  font-family: monospace;
                  font-variant-numeric: tabular-nums;
                  color: ${getChangeColor(stock.changeYtd)};
                `}
              >
                YTD {formatChange(stock.changeYtd)}
              </span>
            </div>

            {/* Row 2: Name, Price, Market Cap */}
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                color: ${colors.silver};
                ${fontStyles.mouse}

                html[data-theme='dark'] & {
                  color: ${colors.tungsten};
                }
              `}
            >
              <span
                css={css`
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  flex: 1;
                  margin-right: ${spacing.centi};
                `}
              >
                {stock.name}
              </span>
              <span
                css={css`
                  display: flex;
                  gap: ${spacing.deci};
                  flex-shrink: 0;
                  font-family: monospace;
                  font-variant-numeric: tabular-nums;
                `}
              >
                <span
                  css={css`
                    color: ${colors.liquorice};

                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {stock.price ? `$${typeof stock.price === 'number' ? stock.price.toFixed(2) : stock.price}` : '—'}
                </span>
                <span>{stock.marketCap ? formatLargeNumber(stock.marketCap) : '—'}</span>
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Handle legacy format - single stock with error handling
  let stockData;
  try {
    stockData = output.data || output;

    if (Array.isArray(stockData)) {
      stockData = stockData[0];
    }

    if (!stockData || typeof stockData !== 'object') {
      return (
        <div
          css={css`
            ${fontStyles.cat}
            color: ${colors.silver};
          `}
        >
          Stock info retrieved
        </div>
      );
    }
  } catch (error) {
    console.error('Failed to parse legacy stock data:', error);
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.berry};
        `}
      >
        Error retrieving stock info
      </div>
    );
  }

  const symbol = stockData.symbol || stockData.ticker || 'N/A';
  const name = stockData.name || stockData.company_name || '';
  const price = stockData.latest_price || stockData.price || stockData.last_price;
  const changeYtd = stockData.change_percent_ytd;
  const marketCap = stockData.market_cap;

  // For single stock, use same card format
  return (
    <div
      css={css`
        ${fontStyles.cat}
        color: ${colors.liquorice};
        padding: ${spacing.centi} ${spacing.deci};

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {/* Row 1: Symbol and YTD Change */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${spacing.milli};
        `}
      >
        <button
          type="button"
          css={css`
            background: none;
            border: none;
            padding: 0;
            font-weight: 600;
            color: ${colors.petrolBlue};
            cursor: pointer;
            text-decoration: none;
            text-align: left;
            font-size: inherit;
            font-family: inherit;

            &:hover,
            &:focus {
              text-decoration: underline;
            }

            &:focus {
              outline: 2px solid ${colors.petrolBlue};
              outline-offset: 2px;
            }

            html[data-theme='dark'] & {
              color: ${colors.skyBlue};

              &:focus {
                outline-color: ${colors.skyBlue};
              }
            }
          `}
          onClick={() => handleSymbolClick(symbol)}
          aria-label={`View details for ${symbol} - ${name}`}
        >
          {symbol}
        </button>
        <span
          css={css`
            font-weight: 500;
            font-family: monospace;
            font-variant-numeric: tabular-nums;
            color: ${getChangeColor(changeYtd)};
          `}
        >
          YTD {formatChange(changeYtd)}
        </span>
      </div>

      {/* Row 2: Name, Price, Market Cap */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: ${colors.silver};
          ${fontStyles.mouse}

          html[data-theme='dark'] & {
            color: ${colors.tungsten};
          }
        `}
      >
        <span
          css={css`
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            margin-right: ${spacing.centi};
          `}
        >
          {name}
        </span>
        <span
          css={css`
            display: flex;
            gap: ${spacing.deci};
            flex-shrink: 0;
            font-family: monospace;
            font-variant-numeric: tabular-nums;
          `}
        >
          <span
            css={css`
              color: ${colors.liquorice};

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {price ? `$${typeof price === 'number' ? price.toFixed(2) : price}` : '—'}
          </span>
          <span>{marketCap ? formatLargeNumber(marketCap) : '—'}</span>
        </span>
      </div>
    </div>
  );
};
